import appConstants from '@appConstants';
import { getDataWithQueryString } from '@componentAPIs';
import { RMAXLogger } from '@RMAXLogger';
import { shouldHandleAsTrailerMovePickup } from '../../../../header/dispatchHeader/utils';
import type { SwapBoardDispatchStore, SwapBoardUpdatePayload } from './types';
import { normalizeTrailerMovesUpdatedPayload } from './swapBoardPayloads';
import {
  updateSwapBoardFromSignalR,
  applySwapBoardPickupAssignEvent,
  applySwapBoardPickupUpdateEvent,
} from './utils';

const loadingState = {
  isLoading: false,
  pendingReload: false,
};

export const resetSwapBoardLoadingState = () => {
  loadingState.isLoading = false;
  loadingState.pendingReload = false;
};

export const getSwapTrailersSnapshot = (dispatchStore?: SwapBoardDispatchStore | null) =>
  dispatchStore?.swapTrailers?.slice?.() ?? [];

export const loadSwapBoard = (dispatchStore: SwapBoardDispatchStore | null | undefined, terminalCode?: string) => {
  if (!dispatchStore || !terminalCode) {
    return;
  }
  if (loadingState.isLoading) {
    loadingState.pendingReload = true;
    return;
  }

  loadingState.isLoading = true;
  dispatchStore.replaceSwapTrailers({
    nextSwapTrailers: getSwapTrailersSnapshot(dispatchStore),
    loadStatus: appConstants.GRID_STATUS.LOADING,
  });

  const finishLoading = () => {
    loadingState.isLoading = false;
    if (loadingState.pendingReload) {
      loadingState.pendingReload = false;
      loadSwapBoard(dispatchStore, terminalCode);
    }
  };

  return getDataWithQueryString('GetPickupsForSwapBoard', `terminalCode=${terminalCode}`).then(
    (response) => {
      if (response.status >= 200 && response.status <= 400) {
        dispatchStore.replaceSwapTrailers({
          nextSwapTrailers: response.data ?? [],
          loadStatus: appConstants.GRID_STATUS.SUCCESS,
        });
      } else {
        dispatchStore.replaceSwapTrailers({
          nextSwapTrailers: [],
          loadStatus: appConstants.GRID_STATUS.ERROR,
        });
      }
      finishLoading();
    },
    (error: Error) => {
      RMAXLogger.error('Trailer_Moves_Load', {
        message: error?.message,
        stack: error?.stack,
        terminalCode,
      });
      dispatchStore.replaceSwapTrailers({
        nextSwapTrailers: [],
        loadStatus: appConstants.GRID_STATUS.ERROR,
      });
      finishLoading();
    }
  );
};

export const refreshSwapBoard = (dispatchStore: SwapBoardDispatchStore | null | undefined, terminalCode?: string) => {
  if (!dispatchStore) {
    return;
  }
  dispatchStore.resetSwapBoard();
  resetSwapBoardLoadingState();
  return loadSwapBoard(dispatchStore, terminalCode);
};

export const handleTrailerMovesUpdatedEvent = ({
  dispatchStore,
  terminalCode,
  payload,
}: {
  dispatchStore: SwapBoardDispatchStore | null | undefined;
  terminalCode: string | undefined;
  payload: SwapBoardUpdatePayload;
}) => {
  if (!dispatchStore) {
    return;
  }
  const normalizedPayload = normalizeTrailerMovesUpdatedPayload(payload);
  if (!normalizedPayload.pickupId && !normalizedPayload.pickupIds?.length) {
    return loadSwapBoard(dispatchStore, terminalCode);
  }

  const updateResult = updateSwapBoardFromSignalR(
    getSwapTrailersSnapshot(dispatchStore),
    normalizedPayload
  );
  if (dispatchStore.applySwapBoardUpdate(updateResult)) {
    return;
  }
  return loadSwapBoard(dispatchStore, terminalCode);
};

export const handleTrailerMovesPickupEvent = ({
  dispatchStore,
  terminalCode,
  dataString,
  eventName,
}: {
  dispatchStore: SwapBoardDispatchStore | null | undefined;
  terminalCode: string | undefined;
  dataString: SwapBoardUpdatePayload;
  eventName: string;
}) => {
  if (!dispatchStore) {
    return;
  }

  const handledEvents = [
    appConstants.PICKUP_EVENTS.ASSIGN_PICKUP,
    appConstants.PICKUP_EVENTS.UNASSIGN_PICKUP,
    appConstants.PICKUP_EVENTS.UPDATE_PICKUP,
  ];

  if (!handledEvents.includes(eventName)) {
    return;
  }

  let pickupData: SwapBoardUpdatePayload;
  if (typeof dataString === 'string') {
    try {
      pickupData = JSON.parse(dataString);
    } catch {
      return;
    }
  } else if (dataString && typeof dataString === 'object') {
    pickupData = dataString;
  } else {
    return;
  }

  if (!pickupData.success) {
    // Caller already gates on enableTrailerSwapBoard; pass true for the type check only.
    const isTrailerMoveEvent =
      shouldHandleAsTrailerMovePickup(pickupData, true) ||
      shouldHandleAsTrailerMovePickup(pickupData.data, true);
    if (isTrailerMoveEvent) {
      return loadSwapBoard(dispatchStore, terminalCode);
    }
    return;
  }

  const groups = getSwapTrailersSnapshot(dispatchStore);
  const updateResult =
    eventName === appConstants.PICKUP_EVENTS.UPDATE_PICKUP
      ? applySwapBoardPickupUpdateEvent(groups, pickupData)
      : applySwapBoardPickupAssignEvent({ apiGroups: groups, pickupData, eventName });

  if (dispatchStore.applySwapBoardUpdate(updateResult)) {
    return;
  }
  return loadSwapBoard(dispatchStore, terminalCode);
};
